/**
 * @fileoverview Helper Blade Component - Slide-out panel for checklist guidance
 * @module HelperBlade
 * 
 * Business Context:
 * - Provides enhanced guidance for maintenance checklist items
 * - Includes photos, step-by-step instructions, and Texas-specific context
 * - Helps users accurately document their home's condition for appeals
 * 
 * Technical Context:
 * - Slide-out panel from right side
 * - Click outside, Escape key, or X button to close
 * - Mobile responsive (full-width on small screens)
 */

import React, { useEffect, useCallback } from 'react';
import { X, Home, Layers, Wind, Droplets, Zap } from 'lucide-react';
import HelperContent from './HelperContent';
import { getHelperContent, CHECKLIST_QUESTIONS } from './helper-content';

export type MaintenanceCategory = 'ROOF' | 'FOUNDATION' | 'HVAC' | 'PLUMBING' | 'ELECTRICAL';

interface HelperBladeProps {
  isOpen: boolean;
  onClose: () => void;
  itemId: string;
  category: MaintenanceCategory;
}

const categoryConfig: Record<MaintenanceCategory, { 
  icon: React.ElementType; 
  color: string; 
  bgColor: string;
  borderColor: string;
  label: string;
}> = {
  ROOF: { 
    icon: Home, 
    color: 'text-red-600', 
    bgColor: 'bg-red-50',
    borderColor: 'border-red-200',
    label: 'Roof'
  },
  FOUNDATION: { 
    icon: Layers, 
    color: 'text-orange-600', 
    bgColor: 'bg-orange-50',
    borderColor: 'border-orange-200',
    label: 'Foundation'
  },
  HVAC: { 
    icon: Wind, 
    color: 'text-blue-600', 
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    label: 'HVAC'
  },
  PLUMBING: { 
    icon: Droplets, 
    color: 'text-cyan-600', 
    bgColor: 'bg-cyan-50',
    borderColor: 'border-cyan-200',
    label: 'Plumbing'
  },
  ELECTRICAL: { 
    icon: Zap, 
    color: 'text-yellow-600', 
    bgColor: 'bg-yellow-50',
    borderColor: 'border-yellow-200',
    label: 'Electrical'
  }
};

const HelperBlade: React.FC<HelperBladeProps> = ({
  isOpen,
  onClose,
  itemId,
  category
}) => {
  // Handle escape key
  const handleEscapeKey = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape' && isOpen) {
      onClose();
    }
  }, [isOpen, onClose]);

  useEffect(() => {
    document.addEventListener('keydown', handleEscapeKey);
    return () => document.removeEventListener('keydown', handleEscapeKey);
  }, [handleEscapeKey]);

  // Prevent body scroll when blade is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const config = categoryConfig[category];
  const Icon = config.icon;
  const content = getHelperContent(itemId);
  const questionText = CHECKLIST_QUESTIONS[itemId] || itemId;

  return (
    <>
      {/* Backdrop */}
      <div 
        className={`fixed inset-0 bg-black/40 z-40 transition-opacity duration-200 ${
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Blade Panel */}
      <div
        className={`fixed right-0 top-0 h-full z-50 transform transition-transform duration-300 ease-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="helper-blade-title"
      >
        <div className="h-full w-screen md:w-[420px] bg-white shadow-2xl flex flex-col">
          {/* Header */}
          <div className={`p-5 border-b ${config.borderColor} ${config.bgColor}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3 flex-1">
                <div className={`p-2.5 rounded-lg ${config.bgColor} border ${config.borderColor}`}>
                  <Icon className={`w-5 h-5 ${config.color}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-xs font-medium ${config.color} uppercase tracking-wide mb-1`}>
                    {config.label} Inspection
                  </p>
                  <h2 
                    id="helper-blade-title" 
                    className="text-lg font-bold text-slate-900 leading-tight"
                  >
                    {questionText}
                  </h2>
                </div>
              </div>
              <button 
                onClick={onClose}
                className="p-2 hover:bg-white/50 rounded-lg transition-colors flex-shrink-0"
                aria-label="Close helper panel"
              >
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>
          </div>

          {/* Content - Scrollable */}
          <div className="flex-1 overflow-y-auto">
            {content ? (
              <HelperContent content={content} category={category} />
            ) : (
              <div className="p-6 text-center text-slate-500">
                <p>No additional help content available for this item.</p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-4 border-t border-slate-200 bg-slate-50">
            <button
              onClick={onClose}
              className="w-full py-3 px-4 bg-slate-900 text-white rounded-lg font-semibold hover:bg-slate-800 transition-colors"
            >
              Got It
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default HelperBlade;
